import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import KeystoneLogo, { KeystoneMark } from '../components/KeystoneLogo';
import { downloadServerPDF } from '../lib/pdf-download';

/**
 * Conversation Brochure v5, visual-forward redesign.
 *
 * Kept alongside v4 (BrochureSalesV4.jsx) so both can be compared
 * side-by-side. This version applies the visual-hierarchy critique:
 *   - one idea, one visual anchor, one thing to remember per page
 *   - real evidence (Keystone Sandbox screens + Cashel Blue photography)
 *     rather than stock photos or SaaS illustrations
 *   - SVG process diagrams instead of stacked text boxes
 *   - QR code on the CTA page for print / PDF-on-another-screen readers
 *
 * All product screenshots come from files that carry the visible
 * "PUBLIC SANDBOX" watermark so no live tenant data is exposed. Cashel
 * Blue photography is used with the written permission of J&L Grubb Ltd
 * per /site/legal.
 */

const TOTAL_PAGES = 5;

const C = {
  navy: '#0A2540',
  navyDeep: '#061A2E',
  magenta: '#E91E63',
  gold: '#C9A961',
  cream: '#FAF8F5',
  paper: '#FFFFFF',
  ink: '#0F1B2A',
  slate: '#3D4A5C',
  muted: '#6B7280',
  border: '#E2E8F0',
  green: '#166534',
};

const Page = ({ children, bg = '#fff', last = false, pageNum, noPad = false }) => (
  <section
    className="brochure-page"
    data-page={pageNum}
    style={{
      width: '210mm',
      height: '297mm',
      background: bg,
      position: 'relative',
      overflow: 'hidden',
      pageBreakAfter: last ? 'auto' : 'always',
      breakAfter: last ? 'auto' : 'page',
      boxShadow: '0 8px 32px rgba(10, 37, 64, 0.12)',
      margin: '0 auto 24px',
      padding: noPad ? 0 : '14mm 16mm',
      boxSizing: 'border-box',
      fontFamily: "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
      color: C.ink,
    }}
  >
    {children}
  </section>
);

const PageHeader = ({ pageNum, dark = false }) => (
  <div
    style={{
      position: 'absolute',
      top: '8mm', left: '16mm', right: '16mm',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '8.5pt',
      color: dark ? 'rgba(255,255,255,0.65)' : C.muted,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      fontWeight: 600,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <KeystoneMark size={20} variant={dark ? 'dark' : 'light'} />
      <span style={{ color: dark ? '#fff' : C.navy, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'none', fontSize: '9.5pt' }}>Keystone</span>
      <span style={{ fontSize: '7.5pt' }}>·</span>
      <span style={{ color: dark ? C.gold : C.magenta, fontSize: '7.5pt', letterSpacing: '0.15em' }}>BY CLOUDVORO</span>
    </div>
    <span>Conversation brochure · v5 · {pageNum} / {TOTAL_PAGES}</span>
  </div>
);

const PageFooter = ({ dark = false }) => (
  <div
    style={{
      position: 'absolute',
      bottom: '8mm', left: '16mm', right: '16mm',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '7.5pt',
      color: dark ? 'rgba(255,255,255,0.55)' : C.muted,
      letterSpacing: '0.06em',
    }}
  >
    <span>keystone.cloudvoro.com</span>
    <span>Cloudvoro Ltd &middot; Thurles, Co. Tipperary, Ireland</span>
  </div>
);

// ==================== PAGE 1: Cover ====================

const CoverPage = () => (
  <Page pageNum={1} bg={C.navy} noPad>
    <div style={{ padding: '14mm 16mm', height: '100%', display: 'flex', flexDirection: 'column', color: '#fff', position: 'relative' }}>
      <PageHeader pageNum={1} dark />

      {/* Atmospheric cheese-maturation photo band along the right edge,
          low opacity so headline sits on top comfortably */}
      <div style={{
        position: 'absolute', top: 0, right: 0, bottom: 0, width: '40%',
        backgroundImage: "url('/press/cheese-maturation.jpg')",
        backgroundSize: 'cover', backgroundPosition: 'center',
        opacity: 0.28,
      }} aria-hidden="true" />
      <div style={{
        position: 'absolute', top: 0, right: 0, bottom: 0, width: '55%',
        background: `linear-gradient(90deg, ${C.navy} 25%, transparent 100%)`,
      }} aria-hidden="true" />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingTop: '18mm', position: 'relative', zIndex: 2 }}>
        <div style={{ marginBottom: '8mm' }}>
          <KeystoneLogo variant="dark" size={40} />
        </div>
        <div style={{ fontSize: '9.5pt', letterSpacing: '0.28em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: '8mm' }}>
          A conversation, not a pitch
        </div>
        <h1 style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: '46pt', fontWeight: 700, color: '#fff', lineHeight: 1.06, letterSpacing: '-0.022em', margin: 0, maxWidth: '150mm',
        }}>
          Your factory<br/>already works.<br/>
          <em style={{ fontStyle: 'italic', color: C.gold, fontWeight: 500 }}>Keystone makes it traceable.</em>
        </h1>
        <div style={{ marginTop: '10mm', fontSize: '13pt', color: 'rgba(255,255,255,0.9)', lineHeight: 1.55, maxWidth: '130mm', fontWeight: 400 }}>
          The operational middle between the factory floor and accounts. Production, dispatch and traceability for growing food manufacturers.
        </div>
        <div style={{ marginTop: '14mm', padding: '5mm 6mm', background: 'rgba(255,255,255,0.06)', borderLeft: `3px solid ${C.gold}`, maxWidth: '135mm', backdropFilter: 'blur(4px)' }}>
          <div style={{ fontSize: '9pt', letterSpacing: '0.2em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: '2mm' }}>
            Built inside a working food factory
          </div>
          <div style={{ fontSize: '10.5pt', lineHeight: 1.6, color: 'rgba(255,255,255,0.92)' }}>
            Written on the production floor of Cashel Farmhouse Cheesemakers in Fethard, Co. Tipperary, against a real Tuesday-morning order deadline.
          </div>
        </div>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6mm',
        marginBottom: '10mm', position: 'relative', zIndex: 2,
        fontSize: '10pt', color: 'rgba(255,255,255,0.75)',
      }}>
        <div>
          <div style={{ fontSize: '8pt', letterSpacing: '0.16em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: 3 }}>Live in</div>
          <div style={{ color: '#fff', fontWeight: 600, fontSize: '11pt' }}>Weeks, not quarters</div>
        </div>
        <div>
          <div style={{ fontSize: '8pt', letterSpacing: '0.16em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: 3 }}>Start with</div>
          <div style={{ color: '#fff', fontWeight: 600, fontSize: '11pt' }}>A 20-minute operational fit review</div>
        </div>
      </div>
      <PageFooter dark />
    </div>
  </Page>
);

// ==================== PAGE 2: Chaos flow ====================

const ChaosPage = () => (
  <Page pageNum={2} bg={C.cream}>
    <PageHeader pageNum={2} />
    <div style={{ paddingTop: '18mm' }}>
      <div style={{ fontSize: '9pt', letterSpacing: '0.24em', textTransform: 'uppercase', fontWeight: 700, color: C.magenta, marginBottom: '5mm' }}>
        02 · Sound familiar?
      </div>
      <h2 style={{
        fontFamily: "'Playfair Display', Georgia, serif", fontSize: '26pt', fontWeight: 700, color: C.navy,
        lineHeight: 1.12, letterSpacing: '-0.018em', margin: '0 0 4mm',
      }}>
        Running a small food business is a paper factory<br/>
        <em style={{ fontStyle: 'italic', color: C.magenta, fontWeight: 500 }}>bolted onto a food factory.</em>
      </h2>

      {/* Chaotic flow diagram, hand-drawn feel via a wobbly SVG path */}
      <div style={{ marginTop: '10mm', width: '100%', height: '75mm', position: 'relative' }}>
        <svg viewBox="0 0 800 320" width="100%" height="100%" style={{ display: 'block' }} preserveAspectRatio="xMidYMid meet">
          {/* Wobbly path connecting the six stops */}
          <path
            d="M 40,160 C 100,60 160,240 240,130 C 320,30 400,250 490,140 C 570,50 640,240 720,130 L 760,170"
            fill="none"
            stroke={C.magenta}
            strokeWidth="3"
            strokeDasharray="8 6"
            strokeLinecap="round"
          />
          {/* Six stops as labelled dots */}
          {[
            { x: 40, y: 160, l: 'EMAIL' },
            { x: 180, y: 160, l: 'EXCEL' },
            { x: 320, y: 160, l: 'WHATSAPP' },
            { x: 460, y: 160, l: 'PAPER' },
            { x: 600, y: 160, l: 'ONE PERSON' },
            { x: 750, y: 160, l: 'AUDITOR' },
          ].map((n, i) => (
            <g key={i}>
              <circle cx={n.x} cy={n.y} r="14" fill={C.navy} />
              <circle cx={n.x} cy={n.y} r="5.5" fill={C.gold} />
              <text x={n.x} y={n.y + 40} fill={C.navy} fontSize="15" fontWeight="700" textAnchor="middle" letterSpacing="1.4" fontFamily="Inter, sans-serif">{n.l}</text>
            </g>
          ))}
        </svg>
      </div>

      {/* Three moments — only three, not four boxes */}
      <div style={{ marginTop: '6mm', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '5mm' }}>
        {[
          { t: '15:47', s: 'FRIDAY', b: 'A retailer wants last Wednesday\u2019s batches, formatted their way, before Monday.' },
          { t: '21:30', s: 'SUNDAY', b: 'Tomorrow\u2019s production plan is still in someone\u2019s head, being reconstructed from order emails.' },
          { t: '\u2014', s: 'AUDIT DAY', b: 'The auditor asks for the trace on one batch. Everyone stops for two hours.' },
        ].map((m, i) => (
          <div key={i} style={{ padding: '5mm 5mm 4mm', background: C.paper, border: `1px solid ${C.border}`, borderTop: `3px solid ${C.magenta}` }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: '2mm' }}>
              <span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '20pt', fontWeight: 700, color: C.magenta, letterSpacing: '-0.02em', lineHeight: 1 }}>{m.t}</span>
              <span style={{ fontSize: '8pt', letterSpacing: '0.22em', color: C.muted, fontWeight: 700 }}>{m.s}</span>
            </div>
            <div style={{ fontSize: '10pt', color: C.slate, lineHeight: 1.55 }}>{m.b}</div>
          </div>
        ))}
      </div>

      {/* Bottom dark block */}
      <div style={{ marginTop: '8mm', padding: '6mm 7mm', background: C.navy, color: '#fff' }}>
        <div style={{ fontSize: '9pt', letterSpacing: '0.22em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: '3mm' }}>
          The real question
        </div>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '19pt', fontWeight: 700, lineHeight: 1.25, color: '#fff', letterSpacing: '-0.012em' }}>
          Should the people who make our food<br/>
          <span style={{ fontStyle: 'italic', color: C.gold, fontWeight: 500 }}>spend their time making it, or reconstructing what happened afterwards?</span>
        </div>
      </div>
    </div>
    <PageFooter />
  </Page>
);

// ==================== PAGE 3: Spine + real screen ====================

const SpineNode = ({ n, t, sub, active }) => (
  <div style={{ textAlign: 'center', flex: 1, position: 'relative', zIndex: 2 }}>
    <div style={{
      width: 44, height: 44, borderRadius: '50%',
      background: active ? C.magenta : C.navy,
      color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: '16pt',
      border: `3px solid ${C.paper}`,
      boxShadow: '0 4px 10px rgba(10,37,64,0.15)',
    }}>{n}</div>
    <div style={{ fontSize: '11pt', fontWeight: 700, color: C.navy, marginTop: '3mm', letterSpacing: '-0.01em' }}>{t}</div>
    <div style={{ fontSize: '8.5pt', color: C.muted, marginTop: 1, letterSpacing: '0.06em' }}>{sub}</div>
  </div>
);

const SpinePage = () => (
  <Page pageNum={3} bg={C.paper}>
    <PageHeader pageNum={3} />
    <div style={{ paddingTop: '18mm' }}>
      <div style={{ fontSize: '9pt', letterSpacing: '0.24em', textTransform: 'uppercase', fontWeight: 700, color: C.magenta, marginBottom: '4mm' }}>
        03 · The spine
      </div>
      <h2 style={{
        fontFamily: "'Playfair Display', Georgia, serif", fontSize: '26pt', fontWeight: 700, color: C.navy,
        lineHeight: 1.12, letterSpacing: '-0.018em', margin: '0 0 3mm',
      }}>
        Four stages, the way your factory<br/>
        <em style={{ fontStyle: 'italic', color: C.magenta, fontWeight: 500 }}>already works.</em>
      </h2>
      <p style={{ fontSize: '10.5pt', color: C.slate, lineHeight: 1.6, margin: '0 0 8mm', maxWidth: '160mm' }}>
        No new operating model. Keystone sits on the operational spine every food manufacturer already has, and makes each stage answer for itself.
      </p>

      {/* Horizontal 4-node process diagram */}
      <div style={{ position: 'relative', padding: '8mm 4mm 4mm', background: C.cream, border: `1px solid ${C.border}` }}>
        <div style={{ position: 'absolute', left: '10%', right: '10%', top: '18mm', height: 2, background: C.magenta, opacity: 0.35, zIndex: 1 }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '4mm' }}>
          <SpineNode n="01" t="Inwards" sub="SUPPLIER LOTS · QC" />
          <SpineNode n="02" t="Production" sub="MAKE-DAYS · BATCHES" />
          <SpineNode n="03" t="Outwards" sub="ORDERS · DISPATCH" />
          <SpineNode n="04" t="Traceability" sub="FORWARD · BACKWARD" active />
        </div>
        <div style={{ marginTop: '6mm', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4mm' }}>
          {[
            'Every ingredient logged with its lot code the moment it arrives.',
            'Real make-days, real weights, real yields, real operator.',
            'Retailer-formatted dispatch notes. Your SKU + their SKU, translated once.',
            'Any batch, forward to every customer. Any customer, backward to the milk.',
          ].map((b, i) => (
            <div key={i} style={{ fontSize: '9pt', color: C.slate, lineHeight: 1.5, textAlign: 'center' }}>{b}</div>
          ))}
        </div>
      </div>

      {/* Real screen (Sandbox) */}
      <div style={{ marginTop: '8mm' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '3mm' }}>
          <div style={{ fontSize: '9pt', letterSpacing: '0.2em', textTransform: 'uppercase', color: C.magenta, fontWeight: 700 }}>
            Real software, not a mockup
          </div>
          <div style={{ fontSize: '8pt', color: C.muted, letterSpacing: '0.04em' }}>Captured from the Public Sandbox demo tenant &middot; no client data</div>
        </div>
        <img
          src="/sales-primer-shots/05-dispatches-list.png"
          alt="Keystone dispatches list, Public Sandbox demo data with retailer names, PO numbers, statuses and dispatch weights."
          style={{ width: '100%', height: 'auto', display: 'block', border: `1px solid ${C.border}`, background: '#fff' }}
        />
      </div>
    </div>
    <PageFooter />
  </Page>
);

// ==================== PAGE 4: Cashel Blue proof ====================

const ProofPage = () => (
  <Page pageNum={4} bg={C.cream} noPad>
    <div style={{ padding: '14mm 16mm', height: '100%', position: 'relative' }}>
      <PageHeader pageNum={4} />
      <div style={{ paddingTop: '18mm', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8mm', alignItems: 'stretch', height: 'calc(100% - 32mm)' }}>
        {/* Left: real photograph */}
        <div style={{ position: 'relative', minHeight: '210mm' }}>
          <div style={{
            position: 'absolute', inset: 0,
            backgroundImage: "url('/press/cheese-maturation.jpg')",
            backgroundSize: 'cover', backgroundPosition: 'center',
          }} />
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            padding: '5mm 5mm', background: 'linear-gradient(180deg, transparent, rgba(10,37,64,0.92))',
            color: '#fff',
          }}>
            <div style={{ fontSize: '8pt', letterSpacing: '0.22em', color: C.gold, fontWeight: 700, marginBottom: 3 }}>THE MATURATION CELLAR</div>
            <div style={{ fontSize: '10pt', lineHeight: 1.5, color: 'rgba(255,255,255,0.9)' }}>
              Cashel Blue<sup>®</sup> maturing in Fethard, Co. Tipperary.
            </div>
          </div>
        </div>

        {/* Right: metrics and reference-call block */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: '9pt', letterSpacing: '0.24em', textTransform: 'uppercase', fontWeight: 700, color: C.magenta, marginBottom: '5mm' }}>
            04 · Proof
          </div>
          <h2 style={{
            fontFamily: "'Playfair Display', Georgia, serif", fontSize: '24pt', fontWeight: 700, color: C.navy,
            lineHeight: 1.15, letterSpacing: '-0.018em', margin: '0 0 6mm',
          }}>
            Built with a manufacturer,<br/>
            <em style={{ fontStyle: 'italic', color: C.magenta, fontWeight: 500 }}>not demonstrated to one.</em>
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5mm 5mm', margin: '0 0 6mm' }}>
            {[
              { v: '40+', k: 'INTERNAL SKUS' },
              { v: '9', k: 'EXPORT MARKETS' },
              { v: 'Retail · Foodservice', k: 'CHANNELS' },
              { v: 'Own brand + private label', k: 'BUSINESS MODEL' },
            ].map((m, i) => (
              <div key={i} style={{ paddingLeft: '3.5mm', borderLeft: `3px solid ${C.magenta}` }}>
                <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '19pt', fontWeight: 700, color: C.navy, lineHeight: 1.05, letterSpacing: '-0.02em' }}>{m.v}</div>
                <div style={{ fontSize: '8pt', color: C.muted, marginTop: 2, letterSpacing: '0.08em', fontWeight: 600 }}>{m.k}</div>
              </div>
            ))}
          </div>

          <div style={{ padding: '5mm 5.5mm', background: C.paper, border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: '10pt', color: C.slate, lineHeight: 1.6 }}>
              We didn&rsquo;t ask Cashel Blue what software they wanted. We watched how they worked. Orders arriving from Tesco, Musgrave, foodservice distributors and export markets. Cheese being made. Retailer codes being translated. COAs prepared. Dispatches leaving. Auditors asking questions. Keystone was built around that week.
            </div>
          </div>

          <div style={{ marginTop: '6mm', padding: '5mm 5.5mm', background: C.navy, color: '#fff', flex: 1 }}>
            <div style={{ fontSize: '8.5pt', letterSpacing: '0.22em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: '2mm' }}>
              Don&rsquo;t take our word for it
            </div>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '17pt', fontWeight: 700, lineHeight: 1.2, color: '#fff', letterSpacing: '-0.01em' }}>
              Speak to the people using it.
            </div>
            <div style={{ marginTop: '3mm', fontSize: '9.5pt', color: 'rgba(255,255,255,0.82)', lineHeight: 1.55 }}>
              After a short discovery call we&rsquo;ll broker a 20-minute reference conversation with Cashel Blue&rsquo;s QA and Operations team.
            </div>
          </div>

          <div style={{ marginTop: '4mm', fontSize: '7.5pt', color: C.muted, fontStyle: 'italic', lineHeight: 1.5 }}>
            Cashel Blue<sup>®</sup> and Crozier Blue<sup>®</sup> are registered trademarks of J&amp;L Grubb Ltd.
          </div>
        </div>
      </div>
      <PageFooter />
    </div>
  </Page>
);

// ==================== PAGE 5: Conversation + QR + founder ====================

const ConversationPage = () => (
  <Page pageNum={5} bg={C.navy} noPad last>
    <div style={{ padding: '14mm 16mm', height: '100%', display: 'flex', flexDirection: 'column', color: '#fff', position: 'relative' }}>
      <PageHeader pageNum={5} dark />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingTop: '18mm' }}>
        <div style={{ fontSize: '9.5pt', letterSpacing: '0.26em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: '5mm' }}>
          05 · The conversation
        </div>
        <h1 style={{
          fontFamily: "'Playfair Display', Georgia, serif", fontSize: '48pt', fontWeight: 700,
          color: '#fff', lineHeight: 1.05, letterSpacing: '-0.022em', margin: 0,
        }}>
          Bring us your<br/>
          <em style={{ fontStyle: 'italic', color: C.gold, fontWeight: 500 }}>worst Friday.</em>
        </h1>
        <div style={{ marginTop: '8mm', fontSize: '13pt', color: 'rgba(255,255,255,0.9)', lineHeight: 1.55, maxWidth: '160mm' }}>
          Give us twenty minutes. Tell us how orders arrive, how production gets planned, how dispatch works and what happens when somebody asks for a batch trace. We&rsquo;ll tell you whether Keystone fits. If it doesn&rsquo;t, we&rsquo;ll say so.
        </div>

        {/* Two-column CTA row: text left, QR right */}
        <div style={{ marginTop: '10mm', display: 'grid', gridTemplateColumns: '1fr 42mm', gap: '8mm', alignItems: 'stretch' }}>
          <div style={{ padding: '5.5mm 6mm', background: 'rgba(255,255,255,0.06)', borderLeft: `3px solid ${C.gold}` }}>
            <div style={{ fontSize: '9pt', letterSpacing: '0.22em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: '3mm' }}>
              20-minute operational fit review
            </div>
            <div style={{ fontSize: '10.5pt', lineHeight: 1.6, color: 'rgba(255,255,255,0.9)' }}>
              Not a demo. Not a sales pitch. A working conversation with the person who will implement the system.
            </div>
            <div style={{ marginTop: '3mm', fontSize: '9.5pt', color: 'rgba(255,255,255,0.72)', lineHeight: 1.55, fontStyle: 'italic' }}>
              If there is a fit, we send you a written scope within 48 hours. Then you choose whether to pilot or implement.
            </div>
            <div style={{ marginTop: '4mm', fontSize: '10.5pt' }}>
              <div style={{ fontWeight: 700, color: '#fff' }}>keystone.cloudvoro.com/site/contact</div>
              <div style={{ color: 'rgba(255,255,255,0.7)', marginTop: 1 }}>or reply to <strong style={{ color: C.gold }}>asif@cloudvoro.com</strong></div>
            </div>
          </div>

          {/* QR code block */}
          <div data-testid="v5-qr-block" style={{ background: '#fff', padding: '5mm', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <QRCodeSVG
              value="https://keystone.cloudvoro.com/site/contact"
              size={128}
              bgColor="#FFFFFF"
              fgColor={C.navy}
              level="M"
              includeMargin={false}
            />
            <div style={{ marginTop: '3mm', fontSize: '7.5pt', letterSpacing: '0.14em', color: C.navy, fontWeight: 700, textAlign: 'center' }}>
              SCAN TO BOOK
            </div>
          </div>
        </div>

        {/* Founder attribution */}
        <div style={{ marginTop: '10mm', padding: '5mm 6mm', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', gap: '5mm' }}>
          <img
            src="/team/asif.png"
            alt="Asif Khan, Founder and Implementation Lead"
            data-testid="v5-founder-headshot"
            style={{
              width: 60, height: 60, borderRadius: '50%',
              objectFit: 'cover', objectPosition: 'center top',
              border: `2px solid ${C.gold}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
              flexShrink: 0,
              background: C.navyDeep,
            }}
          />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '11pt', color: '#fff', fontWeight: 700 }}>Asif Khan</div>
            <div style={{ fontSize: '9.5pt', color: 'rgba(255,255,255,0.7)', marginTop: 1 }}>Founder &middot; Implementation Lead</div>
            <div style={{ fontSize: '9pt', color: 'rgba(255,255,255,0.62)', marginTop: 2, fontStyle: 'italic' }}>
              The person who will pick up your call is also the person who will write the scope, sit on your floor and build the system.
            </div>
          </div>
        </div>

        <div style={{ marginTop: '6mm', padding: '4.5mm 5.5mm', background: 'rgba(201,169,97,0.10)', borderLeft: `2px solid ${C.gold}` }}>
          <div style={{ fontSize: '9pt', color: 'rgba(255,255,255,0.85)', lineHeight: 1.6 }}>
            <strong style={{ color: C.gold }}>Prefer the detail first?</strong> The 16-page buyer pack is at <span style={{ color: '#fff', fontWeight: 700 }}>keystone.cloudvoro.com/brochure/buyer-pack</span>.
          </div>
        </div>
      </div>

      <div style={{ borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: '5mm', marginBottom: '10mm', display: 'flex', justifyContent: 'space-between', alignItems: 'end' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <KeystoneLogo variant="dark" size={24} />
          <div>
            <div style={{ fontSize: '9pt', color: 'rgba(255,255,255,0.75)' }}>Cloudvoro Ltd &middot; Irish operations</div>
            <div style={{ fontSize: '8pt', color: 'rgba(255,255,255,0.55)', marginTop: 1 }}>1 Emmett St, Thurles, Co. Tipperary, E41 YH05, Ireland &middot; asif@cloudvoro.com</div>
            <div style={{ fontSize: '7.5pt', color: 'rgba(255,255,255,0.62)', marginTop: 1 }}>CRO 795787 &middot; VAT IE4490902SH</div>
          </div>
        </div>
        <div style={{ fontSize: '9pt', color: C.gold, fontStyle: 'italic', letterSpacing: '0.04em' }}>
          Make the food. Keystone remembers the rest.
        </div>
      </div>
      <PageFooter dark />
    </div>
  </Page>
);

// ==================== Root ====================

function BrochureSalesV5() {
  const [busy, setBusy] = React.useState(false);
  const download = async () => {
    setBusy(true);
    try { await downloadServerPDF('/brochure/sales/v5', 'Keystone-Conversation-Brochure-v5.pdf'); }
    catch (e) { alert(e?.message || 'PDF render failed'); }
    setBusy(false);
  };

  return (
    <div style={{ background: '#F1F5F9', minHeight: '100vh', padding: '24px 0' }} data-testid="brochure-sales-v5">
      <div style={{
        position: 'sticky', top: 12, zIndex: 10, maxWidth: '210mm', margin: '0 auto 20px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8,
        padding: '10px 16px', boxShadow: '0 4px 16px rgba(10,37,64,0.08)',
      }} className="brochure-actions no-print">
        <div style={{ fontSize: 13, color: C.slate }}>
          <strong style={{ color: C.navy }}>Conversation Brochure v5</strong> &middot; visual-forward redesign
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <a href="/brochure/sales/v4" data-testid="v5-compare-v4-link" style={{ padding: '10px 18px', background: '#fff', color: C.navy, border: `1px solid ${C.border}`, borderRadius: 4, fontWeight: 700, fontSize: 12, letterSpacing: '0.06em', textTransform: 'uppercase', textDecoration: 'none' }}>Compare with v4</a>
          <a href="/brochure/buyer-pack" data-testid="v5-buyer-pack-link" style={{ padding: '10px 18px', background: '#fff', color: C.navy, border: `1px solid ${C.border}`, borderRadius: 4, fontWeight: 700, fontSize: 12, letterSpacing: '0.06em', textTransform: 'uppercase', textDecoration: 'none' }}>Buyer pack →</a>
          <button onClick={download} disabled={busy} data-testid="brochure-sales-v5-download-pdf" style={{ padding: '10px 18px', background: C.magenta, color: '#fff', border: 'none', borderRadius: 4, fontWeight: 700, fontSize: 12, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1 }}>
            {busy ? 'Rendering PDF\u2026' : 'Download PDF'}
          </button>
        </div>
      </div>

      <CoverPage />
      <ChaosPage />
      <SpinePage />
      <ProofPage />
      <ConversationPage />

      <style>{`
        @media print {
          html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
          #root, #root > * { background: #fff !important; margin: 0 !important; padding: 0 !important; }
          .no-print { display: none !important; }
          .brochure-page {
            margin: 0 !important;
            box-shadow: none !important;
            border: 0 !important;
            width: 210mm !important;
            height: 297mm !important;
            overflow: hidden !important;
            page-break-after: always;
            break-after: page;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .brochure-page:last-of-type {
            page-break-after: auto;
            break-after: auto;
          }
        }
        @page { size: A4; margin: 0; }
      `}</style>
    </div>
  );
}

export default BrochureSalesV5;
